"use client";

import { useMemo } from "react";
import NextLink from "next/link";
import { Box, Typography } from "@mui/material";
import CoverPlate from "@/src/components/common/broadsheet/cover-plate";
import Kicker from "@/src/components/common/broadsheet/kicker";
import ArchiveIssue from "@/src/_types/archive-issue";
import Volume from "@/src/_types/volume";
import Archive from "@/src/_types/Archive/archive";
import {
  MapToArchiveIssueItems,
  MapToArchiveItems,
} from "@/src/utils/database-helpers/archive-helper";
import { MapToVolumeItems } from "@/src/utils/database-helpers/volume-helper";
import { cdnServeUrl } from "@/src/utils/paths";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairline,
  HEADING_SIZE,
  microActionSx,
  RAIL_LEFT,
  SHEET_GUTTER,
  spineForJournal,
  STICKY_TOP,
  stickyRailSx,
} from "@/src/components/common/broadsheet/tokens";

const byVolumeDesc = (a: Volume, b: Volume) => {
  const numA = parseInt(a.volume_name, 10);
  const numB = parseInt(b.volume_name, 10);
  const isNumA = !isNaN(numA);
  const isNumB = !isNaN(numB);

  if (isNumA && isNumB) return numB - numA;
  if (isNumA) return -1;
  if (isNumB) return 1;
  return a.volume_name.localeCompare(b.volume_name);
};

const byIssueAsc = (a: ArchiveIssue, b: ArchiveIssue) => {
  const numA = parseInt(a.name, 10);
  const numB = parseInt(b.name, 10);
  const isNumA = !isNaN(numA);
  const isNumB = !isNaN(numB);

  if (isNumA && isNumB) return numA - numB;
  if (isNumA) return -1;
  if (isNumB) return 1;
  return a.name.localeCompare(b.name);
};

const yearOf = (issue: ArchiveIssue) => {
  const raw = issue.formatted_publish_date ?? issue.published_date;
  const match = raw ? String(raw).match(/\d{4}/) : null;
  return match ? match[0] : null;
};

const issueHref = (issue: ArchiveIssue) =>
  issue.articlesCount ? (issue.issue_link ?? "#") : (issue.url ?? "#");

export default function ArchivePage({
  journal,
  issuesData,
}: {
  journal: any;
  issuesData: any;
}) {
  const { volumes, grouped, totals } = useMemo(() => {
    const archives: Archive[] = (MapToArchiveItems(issuesData) ?? []).filter(
      (archive: Archive) =>
        archive.id.toString() !== journal.default_issue_id?.toString(),
    );

    const vols: Volume[] = (MapToVolumeItems(archives) ?? []).sort(byVolumeDesc);
    const issues: ArchiveIssue[] =
      MapToArchiveIssueItems({ journal, archives }) ?? [];

    const map = new Map<number, ArchiveIssue[]>();
    issues.forEach((issue) => {
      const list = map.get(issue.volume_id) ?? [];
      list.push(issue);
      map.set(issue.volume_id, list);
    });
    map.forEach((list) => list.sort(byIssueAsc));

    const years = issues.map(yearOf).filter(Boolean) as string[];

    return {
      volumes: vols,
      grouped: map,
      totals: {
        volumes: vols.length,
        issues: issues.length,
        articles: issues.reduce(
          (sum, issue) => sum + (Number(issue.articlesCount) || 0),
          0,
        ),
        from: years.length ? years.reduce((a, b) => (a < b ? a : b)) : null,
        to: years.length ? years.reduce((a, b) => (a > b ? a : b)) : null,
      },
    };
  }, [issuesData, journal]);

  const cover = cdnServeUrl(journal.meta_data?.default_cover_image_thumbnail);
  const spine = spineForJournal(journal);

  const deck = [
    `${totals.volumes} volume${totals.volumes === 1 ? "" : "s"}`,
    `${totals.issues} issue${totals.issues === 1 ? "" : "s"}`,
    totals.articles ? `${totals.articles} articles` : null,
    totals.from && totals.to
      ? totals.from === totals.to
        ? totals.from
        : `${totals.from}–${totals.to}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            columnGap: 1.5,
            rowGap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
            {cover ? (
              <CoverPlate
                src={cover}
                alt=""
                width={72}
                height={96}
                offset={4}
                spine={spine}
              />
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              <Kicker component="p">Bound volumes</Kicker>
              <Typography
                component="h2"
                sx={{
                  m: 0,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: HEADING_SIZE.page,
                  lineHeight: 1.05,
                  letterSpacing: "0.01em",
                  color: "custom.ink",
                }}
              >
                Archive
              </Typography>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  mt: 0.125,
                  fontFamily: FONT_UI,
                  fontSize: "0.75rem",
                  color: "custom.inkMuted",
                }}
              >
                {deck}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: `${RAIL_LEFT} 1fr` },
          columnGap: 2.5,
          rowGap: 1.25,
          alignItems: "start",
          pt: { xs: 1, md: 1.25 },
          "& > *": { minWidth: 0 },
        }}
      >
        <Box
          component="nav"
          aria-label="Jump to volume"
          sx={{
            display: { xs: "none", lg: "block" },
            ...stickyRailSx,
            pr: 1,
            borderRight: "1px solid",
            borderColor: "custom.ruleHairline",
          }}
        >
          <Kicker component="p" sx={{ mb: 0.375 }}>
            Volumes
          </Kicker>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 0.5,
              rowGap: 0.125,
            }}
          >
            {volumes.map((volume) => (
              <Box
                key={volume.volume_id}
                component="a"
                href={`#volume-${volume.volume_id}`}
                sx={{
                  fontFamily: FONT_UI,
                  fontSize: "0.75rem",
                  fontVariantNumeric: "tabular-nums",
                  color: "custom.inkMuted",
                  textDecoration: "none",
                  paddingBlock: "0.25rem",
                  minWidth: "1.5rem",
                  "&:hover": {
                    color: "custom.link",
                    textDecoration: "underline",
                    textUnderlineOffset: "0.15em",
                  },
                }}
              >
                {volume.volume_name}
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            columnCount: { xs: 1, md: 2, xl: 3 },
            columnGap: 2.5,
          }}
        >
          {volumes.map((volume) => {
            const issues = grouped.get(volume.volume_id) ?? [];
            const years = issues.map(yearOf).filter(Boolean) as string[];
            const span = years.length
              ? years[0] === years[years.length - 1]
                ? years[0]
                : `${years[0]}–${years[years.length - 1]}`
              : null;

            return (
              <Box
                key={volume.volume_id}
                id={`volume-${volume.volume_id}`}
                sx={{ breakInside: "avoid", mb: 1.25, scrollMarginTop: STICKY_TOP }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.5,
                    pb: 0.1875,
                    borderBottom: "2px solid",
                    borderColor: "custom.ink",
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      m: 0,
                      flex: 1,
                      minWidth: 0,
                      fontFamily: FONT_DISPLAY,
                      fontWeight: 700,
                      fontSize: "1.0625rem",
                      lineHeight: 1.2,
                      color: "custom.ink",
                    }}
                  >
                    Volume {volume.volume_name}
                  </Typography>
                  {span ? (
                    <Box
                      component="span"
                      sx={{
                        flex: "none",
                        fontFamily: FONT_UI,
                        fontSize: "0.6875rem",
                        color: "custom.subtitleText",
                      }}
                    >
                      {span}
                    </Box>
                  ) : null}
                </Box>

                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {issues.map((issue) => (
                    <Box
                      component="li"
                      key={issue.id}
                      sx={{
                        m: 0,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.375,
                        py: 0.25,
                        ...hairline,
                      }}
                    >
                      <Box
                        component={NextLink}
                        href={issueHref(issue)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontFamily: FONT_TEXT,
                          fontSize: "0.8125rem",
                          lineHeight: 1.35,
                          color: "custom.link",
                          textDecoration: "none",
                          paddingBlock: "0.125rem",
                          "&:hover": {
                            color: "custom.linkHover",
                            textDecoration: "underline",
                            textUnderlineOffset: "0.15em",
                          },
                        }}
                      >
                        Issue {String(issue.name).replace("-", " ")}
                      </Box>

                      <Box
                        component="span"
                        sx={{
                          flex: "none",
                          fontFamily: FONT_UI,
                          fontSize: "0.6875rem",
                          fontVariantNumeric: "tabular-nums",
                          color: "custom.subtitleText",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {issue.formatted_publish_date}
                        {issue.articlesCount
                          ? ` · ${issue.articlesCount}`
                          : ""}
                      </Box>

                      {issue.xml_link ? (
                        <Box
                          component={NextLink}
                          href={issue.xml_link}
                          target="_blank"
                          sx={{
                            ...microActionSx("custom.subtitleText"),
                            flex: "none",
                          }}
                        >
                          XML
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
